import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getCountryBackfillOverview,
  dispatchCountryBackfill,
  getMaintenanceJob,
} from "../api/adminApi";
import { Section, StatCard, StatusBadge, ShortId } from "../components/admin/AdminUI";

// Friendly action metadata (kept aligned with backend _ACTIONS).
const ACTION_META = {
  ai_enrichment: {
    label: "AI Enrichment",
    de: "Reiht den kompletten Rückstand ein (Legal, Risk, AI-Summary). Der kostengedeckelte Worker arbeitet die Queue ab.",
    cost: true,
  },
  market_recompute: {
    label: "Market / Deal-Score",
    de: "Berechnet Market-Signal + Deal-Score für Listings ohne Analyse.",
    cost: false,
  },
  heatmap_rebuild: {
    label: "Heatmap Rebuild",
    de: "Baut Buy/Rent/Yield-Buckets für ALLE Länder neu (inkl. Ziel).",
    cost: false,
  },
};

function JobResult({ job }) {
  if (!job) return null;
  const s = job.output_summary || job.summary || {};
  return (
    <div
      className="mt-3 rounded-lg border border-zinc-700 bg-zinc-900/60 p-3 text-xs"
      data-testid="country-backfill-job-result"
    >
      <div className="flex items-center gap-2">
        <ShortId value={job.ulid || job.job_ulid} length={10} />
        <StatusBadge status={job.status || "running"} />
      </div>
      <pre className="mt-2 overflow-x-auto whitespace-pre-wrap text-zinc-400">
        {JSON.stringify(
          {
            countries: s.countries,
            scanned: s.scanned,
            eligible: s.eligible,
            enqueued: s.enqueued,
            est_cost_usd: s.est_cost_usd,
            total_missing: s.total_missing,
            slices_processed: s.slices_processed,
          },
          null,
          2,
        )}
      </pre>
    </div>
  );
}

export default function AdminCountryBackfillPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState({ country: null, action: null, mode: "dry_run" });
  const [liveJobUlid, setLiveJobUlid] = useState(null);

  const overviewQ = useQuery({
    queryKey: ["admin", "country-backfill", "overview"],
    queryFn: getCountryBackfillOverview,
  });

  // Poll the currently dispatched job every 4s until it terminates.
  const jobQ = useQuery({
    queryKey: ["admin", "country-backfill", "job", liveJobUlid],
    queryFn: () => getMaintenanceJob(liveJobUlid),
    enabled: !!liveJobUlid,
    refetchInterval: (q) => {
      const st = q?.state?.data?.status;
      return st && ["completed", "failed", "error", "conflict"].includes(st)
        ? false
        : 4000;
    },
  });

  const dispatch = useMutation({
    mutationFn: dispatchCountryBackfill,
    onSuccess: (res) => {
      const ulid = res.job_ulid;
      if (ulid) setLiveJobUlid(ulid);
      toast.success(
        `${ACTION_META[res.action]?.label} • ${res.country} • ${res.mode} gestartet`,
      );
      qc.invalidateQueries({ queryKey: ["admin", "country-backfill", "overview"] });
    },
    onError: (err) => {
      const d = err?.response?.data;
      toast.error(d?.error || d?.message || "Backfill konnte nicht gestartet werden");
    },
  });

  const countries = overviewQ.data?.countries || [];
  const actions = overviewQ.data?.actions || [];

  const run = (country, action, mode) => {
    setSelected({ country, action, mode });
    dispatch.mutate({ country, action, mode });
  };

  const totalEstCost = useMemo(
    () => countries.reduce((sum, c) => sum + (c.est_ai_backfill_cost_usd || 0), 0),
    [countries],
  );

  return (
    <div className="space-y-6" data-testid="admin-country-backfill-page">
      <div>
        <h1 className="text-2xl font-semibold text-zinc-100">Country Backfill</h1>
        <p className="mt-1 text-sm text-zinc-400">
          Country-First Wartungsjobs zentral auslösen — pro Land, mit Kostenschätzung
          und Fortschritt. Immer zuerst <span className="text-zinc-200">Dry-Run</span>,
          dann <span className="text-zinc-200">Execute</span>.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          label="Aktive Länder"
          value={countries.length}
          testid="cb-stat-countries"
        />
        <StatCard
          label="Geschätzte AI-Backfill-Kosten (gesamt)"
          value={`$${totalEstCost.toFixed(2)}`}
          hint={`~$${overviewQ.data?.est_cost_usd_per_listing ?? 0}/Listing`}
          testid="cb-stat-cost"
        />
        <StatCard
          label="Backfill-Aktionen"
          value={actions.length}
          testid="cb-stat-actions"
        />
      </div>

      {overviewQ.isLoading && (
        <div className="text-sm text-zinc-500" data-testid="cb-loading">Lade …</div>
      )}
      {overviewQ.isError && (
        <div className="text-sm text-red-400" data-testid="cb-error">
          Overview konnte nicht geladen werden.
        </div>
      )}

      {countries.map((c) => (
        <Section
          key={c.code}
          title={`${c.code} · ${c.display_de}`}
          testid={`cb-country-${c.code}`}
        >
          <div className="mb-3 flex flex-wrap gap-4 text-xs text-zinc-400">
            <span data-testid={`cb-${c.code}-immo`}>
              Immo aktiv: <b className="text-zinc-200">{c.active_listings.immo}</b>
            </span>
            <span data-testid={`cb-${c.code}-auto`}>
              Auto aktiv: <b className="text-zinc-200">{c.active_listings.auto}</b>
            </span>
            <span data-testid={`cb-${c.code}-unenriched`}>
              Nicht angereichert (Immo):{" "}
              <b className="text-zinc-200">{c.un_enriched_immo}</b>
            </span>
            <span data-testid={`cb-${c.code}-est-cost`}>
              Est. AI-Kosten:{" "}
              <b className="text-amber-300">${c.est_ai_backfill_cost_usd}</b>
            </span>
            <span>
              Enrichment:{" "}
              <b className={c.enrichment_enabled ? "text-emerald-400" : "text-zinc-500"}>
                {c.enrichment_enabled ? "AN" : "AUS"}
              </b>
            </span>
            <span>
              Legal-Profil:{" "}
              <b className="text-zinc-200">{c.legal_profile || "—"}</b>
            </span>
          </div>

          <div className="flex flex-wrap gap-3">
            {actions.map((a) => (
              <div
                key={a.action}
                className="rounded-lg border border-zinc-700 bg-zinc-900/40 p-3"
              >
                <div className="text-sm font-medium text-zinc-200">
                  {ACTION_META[a.action]?.label || a.title}
                </div>
                <div className="mt-1 max-w-xs text-xs text-zinc-500">
                  {ACTION_META[a.action]?.de || a.note}
                </div>
                <div className="mt-2 flex gap-2">
                  <button
                    className="rounded-md border border-zinc-600 px-2 py-1 text-xs text-zinc-200 hover:bg-zinc-800 disabled:opacity-50"
                    onClick={() => run(c.code, a.action, "dry_run")}
                    disabled={dispatch.isPending}
                    data-testid={`cb-${c.code}-${a.action}-dryrun`}
                  >
                    Dry-Run
                  </button>
                  <button
                    className="rounded-md bg-amber-600 px-2 py-1 text-xs font-medium text-white hover:bg-amber-500 disabled:opacity-50"
                    onClick={() => run(c.code, a.action, "execute")}
                    disabled={dispatch.isPending}
                    data-testid={`cb-${c.code}-${a.action}-execute`}
                  >
                    Execute
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Section>
      ))}

      {(dispatch.isPending || liveJobUlid) && (
        <Section title="Laufender Job" testid="cb-live-job">
          <div className="text-xs text-zinc-400">
            {selected.action && (
              <span>
                {ACTION_META[selected.action]?.label} • {selected.country} •{" "}
                {selected.mode}
              </span>
            )}
          </div>
          <JobResult job={jobQ.data} />
        </Section>
      )}
    </div>
  );
}
